import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonItem,
  IonLabel,
  IonModal,
  IonRadio,
  IonRadioGroup,
  IonText,
} from "@ionic/react";
import React, { useState } from "react";
import { useLocation } from "react-router";
import { useProfile } from "../data/store/useProfile";

/**
 * First-login privacy consent modal.
 *
 * Blocking modal shown to authenticated users who have no consent record
 * for the current privacy policy version. Captures:
 *   1. An affirmative privacy choice (Public vs Members Only) — no preselection.
 *   2. Agreement to the privacy policy / terms (required).
 *
 * On submit, `useProfile.recordConsent` writes the choice + a consent
 * record (policy version, timestamp) to Firestore, which clears the
 * `needsConsent` flag and dismisses the modal.
 */
const PrivacyConsentModal: React.FC = () => {
  const needsConsent = useProfile((s) => s.needsConsent);
  const recordConsent = useProfile((s) => s.recordConsent);
  const location = useLocation();

  const [choice, setChoice] = useState<"public" | "member" | "">("");
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = (choice === "public" || choice === "member") && agreed && !submitting;

  // Suppress the modal whenever the user is reading the privacy policy
  // itself — otherwise they can't actually see it before agreeing. When
  // they navigate away or close the privacy tab, the modal returns.
  const onPrivacyPage = location.pathname.startsWith("/privacy");
  const isOpen = needsConsent && !onPrivacyPage;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (choice !== "public" && choice !== "member") return;
    setSubmitting(true);
    try {
      await recordConsent(choice);
    } catch (err) {
      console.error("Failed to record consent:", err);
      setSubmitting(false);
    }
  };

  return (
    <IonModal isOpen={isOpen} backdropDismiss={false} aria-label="Privacy choice">
      <IonContent className="ion-padding">
        <IonText>
          <h2 style={{ marginTop: 8 }}>Welcome to Song Share</h2>
          <p>
            Before you get started, please choose how visible you want your
            music and profile to be. You can change this anytime in Account
            settings.
          </p>
        </IonText>

        <IonRadioGroup
          value={choice}
          onIonChange={(e) => setChoice(e.detail.value)}
        >
          <IonItem lines="full">
            <IonRadio slot="start" value="public" aria-label="Public" />
            <IonLabel className="ion-text-wrap">
              <h3 style={{ margin: 0 }}>Public</h3>
              <p style={{ marginTop: 4 }}>
                Your songs, profile, and comments are visible to anyone, including
                non-members. Public songs can be embedded on third-party websites
                (blogs, classroom sites, etc.).
              </p>
            </IonLabel>
          </IonItem>
          <IonItem lines="full">
            <IonRadio slot="start" value="member" aria-label="Members Only" />
            <IonLabel className="ion-text-wrap">
              <h3 style={{ margin: 0 }}>Members Only</h3>
              <p style={{ marginTop: 4 }}>
                Your songs, profile, and comments are visible only to logged-in
                Song Share members. Your work cannot be embedded on external
                websites.
              </p>
            </IonLabel>
          </IonItem>
        </IonRadioGroup>

        <label
          className="consent-checkbox-row"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 20,
            padding: "12px 14px",
            border: "1px solid rgba(0, 0, 0, 0.2)",
            borderRadius: 8,
            background: agreed ? "rgba(21, 96, 189, 0.06)" : "transparent",
            cursor: "pointer",
          }}
        >
          <IonCheckbox
            checked={agreed}
            onIonChange={(e) => setAgreed(e.detail.checked)}
            aria-label="I agree to the privacy policy and terms"
            style={{
              ["--size" as any]: "26px",
              ["--border-width" as any]: "2px",
              ["--border-color" as any]: "#1560bd",
              ["--border-color-checked" as any]: "#1560bd",
              ["--background-checked" as any]: "#1560bd",
              ["--checkmark-color" as any]: "#ffffff",
              ["--border-radius" as any]: "4px",
              flexShrink: 0,
            }}
          />
          <IonLabel className="ion-text-wrap" style={{ margin: 0 }}>
            I have read and agree to the{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              privacy policy and terms of service
            </a>
            .
          </IonLabel>
        </label>

        <IonButton
          expand="block"
          color="primary"
          disabled={!canSubmit}
          onClick={handleSubmit}
          style={{ marginTop: 16 }}
        >
          {submitting ? "Saving…" : "Continue"}
        </IonButton>

        <IonText color="medium">
          <p style={{ fontSize: 12, marginTop: 16 }}>
            You can change your privacy setting at any time from Account
            settings.
          </p>
        </IonText>
      </IonContent>
    </IonModal>
  );
};

export default PrivacyConsentModal;
